"use client";

import * as React from"react";
import { cva, type VariantProps } from"class-variance-authority";

import { cn } from"@/shared/lib/utils";
import { Button } from"@/shared/components/ui/button";
import { Input } from"@/shared/components/ui/input";
import { Textarea } from"@/shared/components/ui/textarea";

function InputGroup({ className, ...props }: React.ComponentProps<"div">) {
 return (
 <div
 data-slot="input-group"
 role="group"
 className={cn(
"border-input has-[[data-slot=input-group-control]:focus-visible]:border-primary has-[[data-slot=input-group-control]:focus-visible]:ring-primary/20 has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[[data-slot][aria-invalid=true]]:border-destructive h-10 rounded-md border shadow-xs transition-[color,box-shadow] has-[[data-slot=input-group-control]:focus-visible]:ring-1 has-[[data-slot][aria-invalid=true]]:ring-[3px] has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3 has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5 [[data-slot=combobox-content]_&]:focus-within:border-inherit [[data-slot=combobox-content]_&]:focus-within:ring-0 group/input-group relative flex w-full min-w-0 items-center outline-none has-[>textarea]:h-auto",
 className,
 )}
 {...props}
 />
 );
}

const inputGroupAddonVariants = cva(
"text-muted-foreground h-auto gap-2 py-1.5 text-sm font-medium group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4 flex cursor-text items-center justify-center select-none",
 {
 variants: {
 align: {
"inline-start":
"pl-2 has-[>button]:ml-[-0.25rem] has-[>kbd]:ml-[-0.15rem] order-first",
"inline-end":
"pr-2 has-[>button]:mr-[-0.25rem] has-[>kbd]:mr-[-0.15rem] order-last",
"block-start":
"px-2.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2 order-first w-full justify-start",
"block-end":
"px-2.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2 order-last w-full justify-start",
 },
 },
 defaultVariants: {
 align:"inline-start",
 },
 },
);

function InputGroupAddon({
 className,
 align ="inline-start",
 ...props
}: React.ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
 return (
 <div
 role="group"
 data-slot="input-group-addon"
 data-align={align}
 className={cn(inputGroupAddonVariants({ align }), className)}
 onClick={(e) => {
 if ((e.target as HTMLElement).closest("button")) {
 return;
 }
 e.currentTarget.parentElement?.querySelector("input")?.focus();
 }}
 {...props}
 />
 );
}

const inputGroupButtonVariants = cva(
"gap-2 text-sm shadow-none flex items-center",
 {
 variants: {
 size: {
 xs:"h-6 gap-1 rounded-[calc(var(--radius)-5px)] px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
 sm:"",
"icon-xs":
"size-6 rounded-[calc(var(--radius)-5px)] p-0 has-[>svg]:p-0",
"icon-sm":"size-8 p-0 has-[>svg]:p-0",
 },
 },
 defaultVariants: {
 size:"xs",
 },
 },
);

function InputGroupButton({
 className,
 type ="button",
 variant ="ghost",
 size ="xs",
 ...props
}: Omit<React.ComponentProps<typeof Button>,"size"|"type"> &
 VariantProps<typeof inputGroupButtonVariants> & {
 type?:"button"|"submit"|"reset";
 }) {
 return (
 <Button
 type={type}
 data-size={size}
 variant={variant}
 className={cn(inputGroupButtonVariants({ size }), className)}
 {...props}
 />
 );
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
 return (
 <span
 className={cn(
"text-muted-foreground gap-2 text-sm [&_svg:not([class*='size-'])]:size-4 flex items-center [&_svg]:pointer-events-none",
 className,
 )}
 {...props}
 />
 );
}

function InputGroupInput({
 className,
 ...props
}: React.ComponentProps<"input">) {
 return (
 <Input
 data-slot="input-group-control"
 className={cn(
"rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 aria-invalid:ring-0 flex-1",
 className,
 )}
 {...props}
 />
 );
}

function InputGroupTextarea({
 className,
 ...props
}: React.ComponentProps<"textarea">) {
 return (
 <Textarea
 data-slot="input-group-control"
 className={cn(
"rounded-none border-0 bg-transparent py-2 shadow-none ring-0 focus-visible:ring-0 aria-invalid:ring-0 flex-1 resize-none",
 className,
 )}
 {...props}
 />
 );
}

export {
 InputGroup,
 InputGroupAddon,
 InputGroupButton,
 InputGroupText,
 InputGroupInput,
 InputGroupTextarea,
};
